import { camelize, snakeize } from 'casing'
import { AxiosError, AxiosResponse } from 'axios'
import type { Dispatch } from 'redux'

import {
  fetchUserProfile,
  getG2ReviewUrl,
  patchUserProfile as oktaPatchUserProfile,
  patchUserProfileIdentityService,
  patchUserWalkthrough,
} from '../../api'
import { errorLogger } from '../../utils/errorLogger'
import {
  RequestFailureMessage,
  UserUpdationFailureMessage,
  UserUpdationSuccessMessage,
} from '../../utils/messagesContants'
import { renameKey } from '../../utils/renameKey'
import { getErrorMessages } from '../../utils/error'
import { updateAppAlert } from '../appAlert/actions'
import { AppAlertState } from '../appAlert/reducer'
import { startLoading, stopLoading } from '../loading/actions'
import { LoadingTypes } from '../loading/reducer'
import {
  AuthProvider,
  IdentityServiceUser,
  UserProfile,
  UserProfileActionTypes,
  UserWalkthrough,
} from './userProfileTypes'

export type { UserProfile, UserWalkthrough, IdentityServiceUser }
export { UserProfileActionTypes, AuthProvider }

const errorMappings: Record<string, string> = {
  'Old Password is not correct': 'Current Password is not correct',
}

const mapProfileToIdentityServicePayload = (
  profile: UserProfile
): Partial<IdentityServiceUser> => ({
  firstname: profile.firstName || '',
  lastname: profile.lastName || '',
  email: profile.email || '',
  timezone: profile.timeZone || '',
})

const mapIdentityServicePayloadToProfile = (
  data: IdentityServiceUser
): UserProfile => ({
  firstName: data.firstname,
  givenName: data.firstname,
  familyName: data.lastname,
  lastName: data.lastname,
  email: data.email,
  timeZone: data.timezone,
  id: data.id,
  idpUserId: data.idp_user_id,
  roles: data.roles,
  walkthroughs: data.walkthroughs,
})

export const getUserProfile =
  (partnerId: string, userId: string) =>
  async (dispatch: Dispatch, getState: () => unknown) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const userProfile = await fetchUserProfile(partnerId, userId)
      dispatch(setUserProfile(mapIdentityServicePayloadToProfile(userProfile)))
    } catch (error) {
      const globalState = getState()
      errorLogger({ globalState: globalState as never })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updateUserProfile =
  (
    partnerId: string,
    idpUserId: string,
    profile: UserProfile,
    authProvider: AuthProvider
  ) =>
  async (dispatch: Dispatch, getState: () => unknown) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    const patchUserProfile =
      authProvider === AuthProvider.OKTA
        ? oktaPatchUserProfile
        : patchUserProfileIdentityService

    const payload =
      authProvider === AuthProvider.AUTH0
        ? mapProfileToIdentityServicePayload(profile)
        : profile
    return patchUserProfile(partnerId, idpUserId, payload)
      .then(res => {
        const updatedData = renameKey(res.data, 'timezone', 'time_zone')
        dispatch(setUserProfile(updatedData))
        const newAlert: AppAlertState = {
          message: UserUpdationSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        }
        dispatch(updateAppAlert(newAlert))
      })
      .catch((error: AxiosError<{ message?: string }>) => {
        const errMessage =
          error.response?.data?.message || UserUpdationFailureMessage
        dispatch(setUserProfileError(errorMappings[errMessage] || errMessage))
        const newAlert: AppAlertState = {
          message: errorMappings[errMessage] || errMessage,
          messageType: 'ERROR',
          autoClose: true,
        }
        dispatch(updateAppAlert(newAlert))

        const globalState = getState()
        errorLogger({ globalState: globalState as never })(error as Error)
      })
      .finally(() => {
        dispatch(stopLoading(LoadingTypes.GENERAL))
      })
  }

export const setUserProfile = (data: UserProfile) => ({
  type: UserProfileActionTypes.UPDATE_USER_PROFILE,
  payload: camelize(data),
})

export const setOktaUserId = (id: string) => ({
  type: UserProfileActionTypes.SET_OKTA_USER_ID,
  payload: id,
})

export const setUserProfileError = (error: unknown) => ({
  type: UserProfileActionTypes.UPDATE_USER_PROFILE_ERROR,
  payload: error,
})

export const setDefaultTimeZone = (defaultTimeZone: string) => ({
  type: UserProfileActionTypes.SET_DEFAULT_TIMEZONE,
  payload: defaultTimeZone,
})

export const fetchG2ReviewUrl =
  ({ partnerId, userId }: { partnerId: string; userId: string }) =>
  async (dispatch: Dispatch, getState: () => unknown) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getG2ReviewUrl(partnerId, userId)
      return { url: response?.data?.g2_url }
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updateUserWalkthrough =
  ({
    partnerId,
    userId,
    data,
    userPartnerId,
    onWalkthroughUpdated,
  }: {
    partnerId: string
    userId: string
    data: UserWalkthrough
    userPartnerId: string
    onWalkthroughUpdated?: (dispatch: Dispatch) => Promise<void>
  }) =>
  async (dispatch: Dispatch, getState: () => unknown) => {
    try {
      await patchUserWalkthrough(partnerId, userId, {
        walkthroughs: snakeize(data),
      })
      await dispatch(getUserProfile(partnerId, userPartnerId) as never)
      if (onWalkthroughUpdated) {
        await onWalkthroughUpdated(dispatch)
      }
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
    }
  }
